import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { IconHome, IconPlus, IconSearch } from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import type { Block, BlockCategory, Division, Estate, Company } from "../lib/types";
import { BlockForm } from "../components/BlockForm";
import { LandBalance } from "../components/LandBalance";
import { Modal } from "../components/Modal";
import { EstateLandHierarchy } from "./EstateLandHierarchy";
import { PalmMasterTab } from "./PalmMasterTab";
import { TphMasterTab } from "./TphMasterTab";

type Filter = "ALL" | BlockCategory;

const filterLabel: Record<Filter, string> = {
  ALL: "All",
  PLANTED: "Planted",
  LAND_CLEARING: "Land clearing",
  UNPLANTED: "Unplanted",
};

const TABS = ["Blocks", "Hierarchy", "Palms", "TPH", "Hectare statement", "Crop statement", "Rainfall", "Map"];

export function EstateLandBlocks() {
  const [filter, setFilter] = useState<Filter>("ALL");
  const [estateFilter, setEstateFilter] = useState<string>("ALL");
  const [divisionFilter, setDivisionFilter] = useState<string>("ALL");
  const [blockSearch, setBlockSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState("Blocks");
  const [newBlockOpen, setNewBlockOpen] = useState(false);
  const navigate = useNavigate();

  const companiesQ = useQuery({
    queryKey: ["companies"],
    queryFn: () => api.list<Company>("/v1/companies"),
  });
  const estatesQ = useQuery({
    queryKey: ["estates"],
    queryFn: () => api.list<Estate>("/v1/estates"),
  });
  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () => api.list<Division>("/v1/divisions"),
  });
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
  });

  const blocks = blocksQ.data?.data ?? [];
  const divisionCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of divisionsQ.data?.data ?? []) m.set(d.id, d.code);
    return m;
  }, [divisionsQ.data]);

  /**
   * Build divisionId → estateId map so we can filter blocks by estate.
   * (Divisions carry the estateId, blocks only carry divisionId.)
   */
  const estateIdByDivisionId = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of divisionsQ.data?.data ?? []) m.set(d.id, d.estateId);
    return m;
  }, [divisionsQ.data]);

  /**
   * Divisions available given the current estate filter. When
   * "All estates" is selected, all divisions appear.
   */
  const availableDivisions = useMemo(() => {
    const all = divisionsQ.data?.data ?? [];
    return estateFilter === "ALL"
      ? all
      : all.filter((d) => d.estateId === estateFilter);
  }, [divisionsQ.data, estateFilter]);

  /**
   * If the user changes estate and the currently-picked division isn't
   * under the new estate, clear the division filter. Runs as a derived
   * effect on the same render.
   */
  useEffect(() => {
    if (
      divisionFilter !== "ALL" &&
      !availableDivisions.some((d) => d.id === divisionFilter)
    ) {
      setDivisionFilter("ALL");
    }
  }, [availableDivisions, divisionFilter]);

  const filtered = useMemo(() => {
    let list = blocks;
    if (estateFilter !== "ALL") {
      list = list.filter(
        (b) => estateIdByDivisionId.get(b.divisionId) === estateFilter,
      );
    }
    if (divisionFilter !== "ALL") {
      list = list.filter((b) => b.divisionId === divisionFilter);
    }
    if (blockSearch.trim()) {
      const q = blockSearch.trim().toLowerCase();
      list = list.filter((b) => b.code.toLowerCase().includes(q));
    }
    if (filter !== "ALL") list = list.filter((b) => b.category === filter);
    return list;
  }, [
    blocks,
    filter,
    estateFilter,
    divisionFilter,
    blockSearch,
    estateIdByDivisionId,
  ]);
  const counts = useMemo(() => {
    const c = { ALL: blocks.length, PLANTED: 0, LAND_CLEARING: 0, UNPLANTED: 0 };
    for (const b of blocks) c[b.category] += 1;
    return c;
  }, [blocks]);

  const totalHa = useMemo(
    () => blocks.reduce((sum, b) => sum + Number(b.hectaresTotal ?? 0), 0),
    [blocks],
  );
  const plantedHa = useMemo(
    () =>
      blocks
        .filter((b) => b.category === "PLANTED")
        .reduce((sum, b) => sum + Number(b.hectaresPlanted ?? 0), 0),
    [blocks],
  );

  // Interactive: clicking a Land Balance category or a Planted-ha stat tile
  // filters the blocks table AND jumps to the Blocks tab.
  const filterAndShowBlocks = (cat: Filter) => {
    setFilter(cat);
    setActiveTab("Blocks");
  };

  const isLoading =
    companiesQ.isLoading || estatesQ.isLoading || divisionsQ.isLoading || blocksQ.isLoading;
  const error = companiesQ.error ?? estatesQ.error ?? divisionsQ.error ?? blocksQ.error;

  if (error) {
    const msg = error instanceof ApiError ? error.message : "Failed to load";
    return (
      <div className="bg-bg-danger text-text-danger border border-border-danger rounded p-4">
        <div className="font-medium mb-1">Couldn't reach the API</div>
        <div className="text-xs">{msg}</div>
        <div className="text-xs mt-2 opacity-75">
          Is the API server running at <code>localhost:3000</code>? Start it with{" "}
          <code>pnpm --filter @smart-harvesting/api-server dev</code>
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm">
      {/* Breadcrumb */}
      <div className="text-xs text-text-secondary mb-1 flex items-center gap-1">
        <IconHome size={13} />
        Demo Tenant · Region 01 · Company 01 · Estate 21 · Division A
      </div>

      {/* Title + action */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-medium">Estate Land Management</div>
        <button
          className="h-8 px-3 text-[13px] rounded border border-border-strong hover:bg-surface-1 flex items-center gap-1"
          onClick={() => setNewBlockOpen(true)}
        >
          <IconPlus size={14} /> New block
        </button>
      </div>

      {/* Land balance reconciliation — clickable */}
      <LandBalance onCategoryClick={filterAndShowBlocks} activeCategory={filter} />

      {/* Stat tiles — clickable */}
      <div className="grid grid-cols-6 gap-2 mb-4">
        <Stat
          label="Companies"
          value={companiesQ.data?.data.length ?? "—"}
          onClick={() => setActiveTab("Hierarchy")}
          hoverHint="Open Hierarchy"
        />
        <Stat
          label="Estates"
          value={estatesQ.data?.data.length ?? "—"}
          onClick={() => setActiveTab("Hierarchy")}
          hoverHint="Open Hierarchy"
        />
        <Stat
          label="Divisions"
          value={divisionsQ.data?.data.length ?? "—"}
          onClick={() => setActiveTab("Hierarchy")}
          hoverHint="Open Hierarchy"
        />
        <Stat
          label="Blocks"
          value={blocksQ.data?.data.length ?? "—"}
          onClick={() => filterAndShowBlocks("ALL")}
          hoverHint="Show all blocks"
        />
        <Stat
          label="Total ha"
          value={totalHa ? totalHa.toFixed(0) : "—"}
          onClick={() => filterAndShowBlocks("ALL")}
          hoverHint="Show all blocks"
        />
        <Stat
          label="Planted ha"
          value={plantedHa ? plantedHa.toFixed(0) : "—"}
          className="text-text-success"
          onClick={() => filterAndShowBlocks("PLANTED")}
          hoverHint="Filter to Planted"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-border mb-3 text-[13px]">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`py-2 border-b-2 transition-colors ${
              activeTab === t
                ? "border-text-primary text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === "Hierarchy" ? (
        <EstateLandHierarchy />
      ) : activeTab === "Palms" ? (
        <PalmMasterTab />
      ) : activeTab === "TPH" ? (
        <TphMasterTab />
      ) : activeTab === "Blocks" ? (
        <>
          {/* Filter pills · category + estate */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3 text-xs">
            {(["ALL", "PLANTED", "LAND_CLEARING", "UNPLANTED"] as Filter[]).map((k) => (
              <FilterPill key={k} active={filter === k} onClick={() => setFilter(k)} variant={k}>
                {filterLabel[k]} <span className="opacity-70 ml-1">{counts[k]}</span>
              </FilterPill>
            ))}
            <div className="mx-2 h-4 w-px bg-border" />
            <label className="flex items-center gap-1 text-text-secondary">
              Estate
              <select
                value={estateFilter}
                onChange={(e) => setEstateFilter(e.target.value)}
                className={`rounded border px-2 py-1 ${
                  estateFilter !== "ALL"
                    ? "border-emerald-500 bg-emerald-50 font-medium"
                    : "border-border bg-surface-2"
                }`}
              >
                <option value="ALL">All estates</option>
                {(estatesQ.data?.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} · {e.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-text-secondary">
              Division
              <select
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
                className={`rounded border px-2 py-1 ${
                  divisionFilter !== "ALL"
                    ? "border-emerald-500 bg-emerald-50 font-medium"
                    : "border-border bg-surface-2"
                }`}
              >
                <option value="ALL">All divisions</option>
                {availableDivisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} · {d.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex-1" />
            <label className="flex items-center gap-1 text-text-secondary">
              <IconSearch size={13} />
              <input
                type="text"
                value={blockSearch}
                onChange={(e) => setBlockSearch(e.target.value)}
                placeholder="Block code…"
                className={`rounded border px-2 py-1 w-32 ${
                  blockSearch
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-border bg-surface-2"
                }`}
              />
            </label>
          </div>

          {/* Active-filter chip · summarises what's applied and offers Clear-all */}
          {(estateFilter !== "ALL" ||
            divisionFilter !== "ALL" ||
            blockSearch.trim()) && (
            <div className="mb-3 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs flex-wrap">
              <span className="font-medium text-emerald-900">
                Showing {filtered.length} of {blocks.length} blocks
              </span>
              {estateFilter !== "ALL" && (
                <FilterChip
                  label={`Estate ${(estatesQ.data?.data ?? []).find((e) => e.id === estateFilter)?.code ?? "?"}`}
                  onClear={() => setEstateFilter("ALL")}
                />
              )}
              {divisionFilter !== "ALL" && (
                <FilterChip
                  label={`Division ${(divisionsQ.data?.data ?? []).find((d) => d.id === divisionFilter)?.code ?? "?"}`}
                  onClear={() => setDivisionFilter("ALL")}
                />
              )}
              {blockSearch.trim() && (
                <FilterChip
                  label={`Code "${blockSearch.trim()}"`}
                  onClear={() => setBlockSearch("")}
                />
              )}
              <button
                onClick={() => {
                  setEstateFilter("ALL");
                  setDivisionFilter("ALL");
                  setBlockSearch("");
                }}
                className="ml-auto rounded border border-emerald-600 px-2 py-0.5 text-[10px] text-emerald-800 hover:bg-emerald-100"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Table */}
          <div className="border border-border rounded overflow-hidden">
            {isLoading && !blocks.length ? (
              <div className="p-6 text-center text-xs text-text-secondary">Loading blocks…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-text-secondary">
                No blocks match this filter. Click <em>+ New block</em> above to add one.
              </div>
            ) : (
              <table className="w-full text-xs">
                <colgroup>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "24%" }} />
                </colgroup>
                <thead>
                  <tr className="bg-surface-1 text-text-secondary text-left">
                    <th className="px-2.5 py-2 font-medium">Div · Code</th>
                    <th className="px-2.5 py-2 font-medium">Category</th>
                    <th className="px-2.5 py-2 font-medium">Planting status</th>
                    <th className="px-2.5 py-2 font-medium">Planting material</th>
                    <th className="px-2.5 py-2 font-medium">Ha</th>
                    <th className="px-2.5 py-2 font-medium">Year</th>
                    <th className="px-2.5 py-2 font-medium">Delivery flags</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      className="border-t border-border hover:bg-surface-1 cursor-pointer"
                      onClick={() => navigate(`/estate-land/blocks/${b.id}`)}
                    >
                      <td className="px-2.5 py-2 font-mono font-medium">
                        <span className="text-text-secondary">
                          {divisionCodeById.get(b.divisionId) ?? "?"}
                        </span>
                        <span className="text-text-muted mx-1">·</span>
                        {b.code}
                      </td>
                      <td className="px-2.5 py-2">
                        <CategoryPill category={b.category} />
                      </td>
                      <td className="px-2.5 py-2">
                        <PlantingStatusPill
                          category={b.category}
                          yearOfPlanting={b.yearOfPlanting}
                        />
                      </td>
                      <td className="px-2.5 py-2">
                        {b.plantingMaterialType ? (
                          <span>
                            <strong className="font-medium">{b.plantingMaterialType}</strong>{" "}
                            <span className="text-text-secondary">
                              {b.plantingMaterialDetails ?? ""}
                            </span>
                          </span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-2.5 py-2">
                        {b.hectaresTotal ? Number(b.hectaresTotal).toFixed(2) : "—"}
                      </td>
                      <td className="px-2.5 py-2">{b.yearOfPlanting ?? "—"}</td>
                      <td className="px-2.5 py-2 flex items-center gap-1.5">
                        {b.innerRoadToMill ? (
                          <span className="bg-bg-warning text-text-warning px-1.5 py-0.5 rounded text-[10px] font-mono">
                            inner_rd {b.millRoadKm ? `· ${b.millRoadKm}km` : ""}
                          </span>
                        ) : null}
                        {b.routeOverride !== "AUTO" ? (
                          <span className="bg-surface-1 border border-border text-text-secondary px-1.5 py-0.5 rounded text-[10px] font-mono">
                            {b.routeOverride}
                          </span>
                        ) : null}
                        {!b.innerRoadToMill && b.routeOverride === "AUTO" ? (
                          <span className="text-text-muted text-[10px]">default</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {filtered.length > 0 && (
              <div className="px-3 py-2 border-t border-border bg-surface-1 text-center text-[11px] text-text-secondary">
                {filtered.length} block{filtered.length === 1 ? "" : "s"} shown · click a row for
                block detail
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="p-8 text-center text-xs text-text-secondary bg-surface-1 rounded">
          <em>{activeTab}</em> — deferred to a later phase. See <code>docs/mockups/</code> for the
          design intent.
        </div>
      )}

      {/* New block modal */}
      <Modal
        open={newBlockOpen}
        onClose={() => setNewBlockOpen(false)}
        title="New block"
        subtitle="Category-specific fields appear below. Server validates cross-field rules."
        maxWidth="600px"
      >
        <BlockForm
          /* If user has picked an estate filter, we can suggest the
             first division under that estate as the default. Otherwise
             BlockForm auto-picks when only 1 division exists overall. */
          defaultDivisionId={
            estateFilter !== "ALL"
              ? (divisionsQ.data?.data ?? []).find(
                  (d) => d.estateId === estateFilter,
                )?.id
              : undefined
          }
          onSaved={() => {
            setNewBlockOpen(false);
          }}
          onCancel={() => setNewBlockOpen(false)}
        />
      </Modal>
    </div>
  );
}

function Stat({
  label,
  value,
  className = "",
  onClick,
  hoverHint,
}: {
  label: string;
  value: string | number;
  className?: string;
  onClick?: () => void;
  hoverHint?: string;
}) {
  return (
    <div
      onClick={onClick}
      title={hoverHint}
      className={`bg-surface-1 rounded p-2.5 group transition-colors ${
        onClick ? "cursor-pointer hover:bg-surface-2 hover:ring-1 hover:ring-border-strong" : ""
      }`}
    >
      <div className="text-[11px] text-text-secondary flex items-center justify-between">
        <span>{label}</span>
        {onClick && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-text-accent text-[9px]">
            →
          </span>
        )}
      </div>
      <div className={`text-lg font-medium ${className}`}>{value}</div>
    </div>
  );
}

function CategoryPill({ category }: { category: BlockCategory }) {
  const cls =
    category === "PLANTED"
      ? "bg-bg-success text-text-success"
      : category === "LAND_CLEARING"
        ? "bg-bg-warning text-text-warning"
        : "bg-surface-1 text-text-secondary border border-border";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[11px] ${cls}`}>
      {category === "LAND_CLEARING"
        ? "Land clearing"
        : category === "PLANTED"
          ? "Planted"
          : "Unplanted"}
    </span>
  );
}

/**
 * Planting-status pill · derives from category + year of planting.
 * Immature: 0–3 years since planting (no yield)
 * Mature:   >3 years and <25 years (main yielding stage)
 * Senile:   ≥25 years (declining yield; replanting candidate)
 * Only shown when block is PLANTED.
 */
function PlantingStatusPill({
  category,
  yearOfPlanting,
}: {
  category: BlockCategory;
  yearOfPlanting: number | null;
}) {
  if (category !== "PLANTED") {
    return <span className="text-text-muted">—</span>;
  }
  if (yearOfPlanting == null) {
    return <span className="text-text-muted">—</span>;
  }
  const age = new Date().getFullYear() - yearOfPlanting;
  let label: string;
  let cls: string;
  if (age < 3) {
    label = `Immature · ${age}y`;
    cls = "bg-amber-100 text-amber-800";
  } else if (age < 25) {
    label = `Mature · ${age}y`;
    cls = "bg-emerald-100 text-emerald-800";
  } else {
    label = `Senile · ${age}y`;
    cls = "bg-red-100 text-red-800";
  }
  return (
    <span className={`px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
}

/** Small inline chip listing one active filter with an ✕ to clear it. */
function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-white px-2 py-0.5 text-[10px] text-emerald-900">
      {label}
      <button
        onClick={onClear}
        className="rounded-full text-emerald-700 hover:bg-emerald-100"
        title="Clear this filter"
      >
        ✕
      </button>
    </span>
  );
}

function FilterPill({
  active,
  onClick,
  variant,
  children,
}: {
  active: boolean;
  onClick: () => void;
  variant: Filter;
  children: React.ReactNode;
}) {
  const base = "px-2.5 py-1 rounded-full cursor-pointer transition-colors";
  const styles = active
    ? variant === "PLANTED"
      ? "bg-bg-success text-text-success font-medium"
      : variant === "LAND_CLEARING"
        ? "bg-bg-warning text-text-warning font-medium"
        : variant === "UNPLANTED"
          ? "bg-surface-1 border border-border-strong"
          : "bg-surface-1 border border-border-strong font-medium"
    : "bg-surface-1 border border-border text-text-secondary hover:border-border-strong";
  return (
    <button className={`${base} ${styles}`} onClick={onClick}>
      {children}
    </button>
  );
}
